import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Stack,
  CircularProgress,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { useAppSelector } from '../../stores/store';
import { stepThreeStyles } from '../../styles/locationCampaign/stepThree';
import background from '../../assets/images/location_campaign/lemn.png';
import kozel from '../../assets/images/location_campaign/logo.png';
import premiu from '../../assets/images/location_campaign/premiu.png';
import despre_alcool from '../../assets/images/promo-despre-alcool.png';
import linie1 from '../../assets/images/location_campaign/linie1.png';
import linie2 from '../../assets/images/location_campaign/linie2.png';
import icon1 from '../../assets/images/location_campaign/icon1.png';
import icon2 from '../../assets/images/location_campaign/icon2.png';

interface StepThreeProps {
  dataBonFiscal: Dayjs | null;
  numarBonFiscal: string;
  incarcaPozaBon: File | null;
  onDateChange: (newDate: Dayjs | null) => void;
  onInputChange: (field: string, value: string) => void;
  onFileChange: (file: File | null) => void;
  onSubmit: () => void;
  errors: { [key: string]: string };
}

export default function StepThree({ 
  dataBonFiscal, 
  numarBonFiscal, 
  incarcaPozaBon,
  onDateChange, 
  onInputChange,
  onFileChange,
  onSubmit,
  errors
}: StepThreeProps) {
  const { loading } = useAppSelector(state => state.locationCampaign);
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
  const theme = useTheme();

  const handleDateFocus = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDatePickerOpen(true);
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    onDateChange(newDate);
    setIsDatePickerOpen(false);
  };

  const handleFileFromGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    onFileChange(file);
  };

  const handleFileFromCamera = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    onFileChange(file);
  };

  const handleSubmit = () => {
    if (!dataBonFiscal || !numarBonFiscal || !incarcaPozaBon ||
      errors.dataBonFiscal || errors.numarBonFiscal || errors.incarcaPozaBon) {
    return;
  }
    
    onSubmit();
  };

  return (
    <Box sx={stepThreeStyles.container}>
      <Paper elevation={0} sx={stepThreeStyles.paper(background)}>
          {/* Header */}
          <Box sx={stepThreeStyles.header}>
            <Box
              component="img"
              src={kozel}
              alt="Kozel"
              sx={stepThreeStyles.logo}
            />
          </Box>

          <Box sx={stepThreeStyles.content}>
            {/* Main Title */}
            <Typography variant="h1" sx={stepThreeStyles.mainTitle(theme)}>
              MEEEEEEREU ÎMPREUNĂ
            </Typography>
            
            <Typography variant="h4" sx={stepThreeStyles.subtitle(theme)}>
              CÂȘTIGĂM PREMII
            </Typography>

           {/* Prize Section */}
            <Box sx={stepThreeStyles.prizeSection}>
              <Typography variant="h6" sx={stepThreeStyles.prizeTitle}>
                TOMBOLĂ LA FINAL DE CAMPANIE
              </Typography>
              
              <Box sx={stepThreeStyles.prizeContent}>
                <Box sx={stepThreeStyles.prizeLeftSection}>
                  <Box sx={stepThreeStyles.prizeCircle}>
                    <Typography sx={stepThreeStyles.prizeCircleNumber}>
                      60
                    </Typography>
                    <Typography sx={stepThreeStyles.prizeCircleText}>
                      x
                    </Typography>
                  </Box>
                  <Typography sx={stepThreeStyles.prizeAmount}>
                    Voucher Emag 500 lei
                  </Typography>
                </Box>
                
                <Box
                  component="img"
                  src={premiu}
                  alt="Premiu Emag 500 LEI"
                  sx={stepThreeStyles.prizeImageInSection}
                />
              </Box>
            </Box>

            {/* Form Title */}
            <Typography variant="h6" sx={stepThreeStyles.formTitle}>
              Completează detaliile bonului fiscal
            </Typography>

            {/* Linia 1 - înainte de input-uri */}
            <Box sx={stepThreeStyles.lineContainer}>
              <Box
                component="img"
                src={linie1}
                alt="Linia 1"
                sx={stepThreeStyles.lineImage}
              />
            </Box>

            {/* Date Picker */}
            <DatePicker
              value={dataBonFiscal}
              onAccept={handleDateChange}
              closeOnSelect={true}
              format="DD/MM/YYYY"
              open={isDatePickerOpen}
              onOpen={() => setIsDatePickerOpen(true)}
              onClose={() => setIsDatePickerOpen(false)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: "Data bonului fiscal",
                  onClick: handleDateFocus,
                  error: Boolean(errors.dataBonFiscal),
                  helperText: errors.dataBonFiscal,
                  sx: stepThreeStyles.datePickerField
                }
              }}
            />

            {/* Linia 2 - după primul input */}
            <Box sx={stepThreeStyles.lineContainer}>
              <Box
                component="img"
                src={linie2}
                alt="Linia 2"
                sx={stepThreeStyles.lineImage}
              />
            </Box>

            {/* Number Field */}
            <TextField
              fullWidth
              placeholder="Număr bon fiscal"
              value={numarBonFiscal}
              onChange={(e) => onInputChange('numarBonFiscal', e.target.value)}
              error={Boolean(errors.numarBonFiscal)}
              helperText={errors.numarBonFiscal}
              sx={stepThreeStyles.textField}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />

            {/* Linia 2 - după al doilea input */}
            <Box sx={stepThreeStyles.lineContainerWithMargin}>
              <Box
                component="img"
                src={linie2}
                alt="Linia 2"
                sx={stepThreeStyles.lineImage}
              />
            </Box>

            {/* File Upload Section */}
            <Box sx={stepThreeStyles.fileUploadSection}>
              <Typography variant="h6" sx={stepThreeStyles.fileUploadTitle}>
                Încarcă poza bonului fiscal
              </Typography>
              
              <Stack direction="row" spacing={4} sx={stepThreeStyles.fileUploadButtonsContainer}>
                <Box
                  component="img"
                  src={icon2}
                  alt="Galerie"
                  onClick={() => document.getElementById('gallery-input')?.click()}
                  sx={stepThreeStyles.uploadIcon}
                />
                
                <Box
                  component="img"
                  src={icon1}
                  alt="Cameră"
                  onClick={() => document.getElementById('camera-input')?.click()}
                  sx={stepThreeStyles.uploadIcon}
                />
              </Stack>

              {incarcaPozaBon && (
                <Box sx={stepThreeStyles.fileSelectedContainer}>
                   {incarcaPozaBon.name}
                </Box>
              )}

              {errors.incarcaPozaBon && (
                <Typography sx={stepThreeStyles.fileErrorText}>
                  {errors.incarcaPozaBon}
                </Typography>
              )}

              <input
                id="gallery-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileFromGallery}
              />
              
              <input
                id="camera-input"
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileFromCamera}
              />
            </Box>

            {/* Submit Button */}
           <Box sx={stepThreeStyles.buttonContainer}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!dataBonFiscal || !numarBonFiscal || !incarcaPozaBon || loading ||
                      Boolean(errors.dataBonFiscal) || Boolean(errors.numarBonFiscal) || Boolean(errors.incarcaPozaBon)}
              sx={stepThreeStyles.submitButton}
            >
              {loading ? (
                <Box sx={stepThreeStyles.loadingContainer}>
                  <CircularProgress size={20} sx={stepThreeStyles.loadingSpinner} />
                  SE PROCESEAZĂ...
                </Box>
              ) : (
                'MĂ ÎNSCRIU'
              )}
            </Button>
          </Box>

            {/* Footer Info */}
            <Box sx={stepThreeStyles.footer}>
              <Box sx={stepThreeStyles.footerImageContainer}>
                <Box
                  component="img"
                  src={despre_alcool}
                  alt="Despre alcool"
                  sx={stepThreeStyles.footerImage}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
    </Box>
  );
}